import { format, parseISO } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { Calendar as CalendarIcon } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Calendar } from '@/components/ui/Calendar';
import { controlTriggerVariants } from '@/components/ui/button-variants';
import { cn } from '@/lib/cn';

type Props = {
  value: string;
  onChange: (isoDate: string) => void;
  disabled?: boolean;
  min?: string;
  max?: string;
  className?: string;
  placeholder?: string;
  'aria-label'?: string;
};

export function DatePicker({
  value,
  onChange,
  disabled,
  min,
  max,
  className,
  placeholder,
  'aria-label': ariaLabel
}: Props) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState<{ top: number; left: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const locale = i18n.language.startsWith('ar') ? ar : enUS;

  const display =
    value && value.length >= 10
      ? format(parseISO(value), 'PPP', { locale })
      : (placeholder ?? t('ui.datePicker.placeholder'));

  useEffect(() => {
    if (!open || !rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    setPanelStyle({ top: rect.bottom + 4, left: rect.left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const target = e.target as Node;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const panel =
    open && panelStyle
      ? createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-label={ariaLabel ?? t('ui.datePicker.label')}
            className="fixed z-[200] rounded-[var(--fv-radius-lg)] border border-[var(--fv-border)] bg-[var(--fv-surface-elevated)] shadow-[var(--fv-shadow-md)]"
            style={{ top: panelStyle.top, left: panelStyle.left }}
          >
            <Calendar
              value={value}
              min={min}
              max={max}
              onSelect={(iso) => {
                onChange(iso);
                setOpen(false);
              }}
            />
          </div>,
          document.body
        )
      : null;

  return (
    <div ref={rootRef} className={cn('relative inline-block w-full min-w-[10rem]', className)}>
      <button
        type="button"
        disabled={disabled}
        aria-label={ariaLabel ?? t('ui.datePicker.label')}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? panelId : undefined}
        className={cn(controlTriggerVariants({ open }), !value && 'text-[var(--fv-text-muted)]')}
        onClick={() => !disabled && setOpen((o) => !o)}
      >
        <span className="truncate">{display}</span>
        <CalendarIcon className="h-4 w-4 shrink-0 text-[var(--fv-text-muted)]" aria-hidden />
      </button>
      {panel}
    </div>
  );
}
